<script setup lang="ts">
import Charger from '@/classes/charger_classes/charger'
import FavouriteLocation from '@/classes/favouriteLocation'
import TextBtn from '@/components/ui-elements/buttons/TextBtn.vue'
import {
  GettersTypes,
  MutationTypes,
  SecondaryDialogContent,
  type State,
} from '@/store/store_types'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import LocationDisplayCard from './LocationDisplayCard.vue'

const { locationType } = defineProps<{
  locationType: 'saved-charger' | 'fav-location' | 'fleet-fav-location'
}>()

const title = computed(() => {
  switch (locationType) {
    case 'saved-charger':
      return 'Saved chargers'
    case 'fleet-fav-location':
      return 'Fleet saved locations'
    case 'fav-location':
      return 'Saved locations'
  }
})

const emptyStateMsg = computed(() => {
  switch (locationType) {
    case 'saved-charger':
      return 'No saved charging stations'
    case 'fleet-fav-location':
      return 'No saved fleet locations'
    case 'fav-location':
      return 'No saved locations'
  }
})

const searchQuery = ref('')
const store = useStore<State>()

const unfilteredLocations = computed<Charger[] | FavouriteLocation[]>(() => {
  if (locationType === 'fav-location')
    return store.state.favLocations.filter((loc) => loc.visibility === 'private')
  if (locationType === 'fleet-fav-location')
    return store.state.favLocations.filter((loc) => loc.visibility === 'fleet')
  if (locationType === 'saved-charger') return store.getters[GettersTypes.getSavedChargers]
  return []
})

const filteredLocations = computed(() => {
  if (searchQuery.value) {
    if (
      locationType === 'fav-location' &&
      unfilteredLocations.value.length &&
      unfilteredLocations.value[0] instanceof FavouriteLocation
    ) {
      return (unfilteredLocations.value as FavouriteLocation[]).filter((location) => {
        if (location.name?.toLowerCase().includes(searchQuery.value.toLowerCase().trim()))
          return true
        if (location.address?.toLowerCase().includes(searchQuery.value.toLowerCase().trim()))
          return true
        return false
      })
    }
    if (
      locationType === 'saved-charger' &&
      unfilteredLocations.value.length &&
      unfilteredLocations.value[0] instanceof Charger
    ) {
      return (unfilteredLocations.value as Charger[]).filter((charger) => {
        if (charger.name?.toLowerCase().includes(searchQuery.value.toLowerCase().trim()))
          return true
        if (charger.address?.toLowerCase().includes(searchQuery.value.toLowerCase().trim()))
          return true
        if (
          charger.bookableEVSEDisplayData.some((evse) =>
            evse.display_name?.includes(searchQuery.value.toLowerCase().trim()),
          )
        )
          return true
        return false
      })
    }
  }
  return unfilteredLocations.value
})

const openNetworkSettings = () => {
  store.commit(MutationTypes.setSecondaryDialogContent, SecondaryDialogContent.NETWORK_SETTINGS)
}

const navigateToLocationDetails = (location: Charger | FavouriteLocation) => {
  if (location instanceof Charger) navigateToChargerDetails(location)
  if (location instanceof FavouriteLocation) navigateToFavouriteLocationDetails(location)
}

const navigateToChargerDetails = (charger: Charger) => {
  store.commit(MutationTypes.setSelectedCharger, charger.id)
  store.commit(MutationTypes.setDashboardDialogContent, 'saved-charger-details')
}

const navigateToFavouriteLocationDetails = (location: FavouriteLocation) => {
  store.commit(MutationTypes.setSelectedFavLocation, location)
  store.commit(
    MutationTypes.setDashboardDialogContent,
    locationType === 'fleet-fav-location' ? 'fleet-location-details' : 'saved-location-details',
  )
}
</script>

<template>
  <v-container class="px-5 bg-pwt_snow">
    <!-- Action buttons -->
    <v-card-text class="d-flex align-center justify-end pb-0">
      <TextBtn :padded="false" @click="openNetworkSettings"> Settings/filters </TextBtn>
      <TextBtn :padded="false"> Browse charging options on map </TextBtn>
    </v-card-text>
    <!-- Search bar -->
    <v-card-text class="mb-5">
      <v-text-field
        v-model="searchQuery"
        clearable
        class="bg-pwt_snow"
        placeholder="Search saved charging stations & locations..."
        hide-details
      ></v-text-field>
    </v-card-text>
    <!-- Saved chargers section -->
    <v-card-title class="mb-3">
      {{ title }}
    </v-card-title>
    <v-card-text v-if="!filteredLocations.length && searchQuery">
      No results found for '{{ searchQuery }}'
    </v-card-text>
    <v-card-text v-else-if="!filteredLocations.length"> {{ emptyStateMsg }}</v-card-text>
    <v-card-text v-else>
      <v-row no-gutters>
        <v-col
          cols="12"
          md="6"
          v-for="(location, index) in filteredLocations"
          :key="'location-card-' + index"
          :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-md-2'"
          class="mb-3"
        >
          <LocationDisplayCard
            :location="location"
            @click="() => navigateToLocationDetails(location)"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-container>
</template>
